import { useCallback, useMemo, useState } from 'react'
import { Alert, AlertIcon } from 'ui/alert'
import { Button } from 'ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'ui/select'
import { Separator } from 'ui/separator'
import { Switch } from 'ui/switch'
import { useToast } from 'ui/use-toast'
import type { ErrorToastHandler } from '../hooks/useError'
import { useMessageTemplateEdit } from '../hooks/useMessageTemplateEdit'
import { useMessageTemplate } from '../hooks/useMessageTemplates'
import {
  IMessageTemplate,
  deliveryChannels,
  messageTemplateSwitchCheckedByStatus,
} from '../providers/MessageTemplate.provider'
import { CreateMessageTemplateV2 } from './CreateMessageTemplateV2'
import { MessageTemplateEditorV2 } from './MessageTemplateEditorV2'
import { TimelineStyles } from './TimelineStyles'
import { useTranslation } from 'i11n/client'

const MessageTemplateGatewayStatus = (props: {
  gatewayStatus?: IMessageTemplate['gatewayStatus']
}) => {
  const t = useTranslation()

  return (
    <div className='max-w-md w-full'>
      {(() => {
        switch (props.gatewayStatus) {
          case 'pending':
            return (
              <Alert className='rounded-lg' status='info'>
                <AlertIcon />
                {t('El proveedor de mensajería está revisando la plantilla')}
              </Alert>
            )

          case 'rejected':
            return (
              <Alert className='items-start rounded-lg' status='error'>
                <AlertIcon className='mt-[5px]' />
                {t(
                  'El proveedor de mensajería rechazó la plantilla. Para saber más detalles, contacta con soporte.',
                )}
              </Alert>
            )

          case 'approved':
            return (
              <Alert status='success'>
                <AlertIcon />
                {t(
                  'El proveedor de mensajería aceptó la plantilla, por lo que la plantilla será tomada en cuenta.',
                )}
              </Alert>
            )

          default:
            return null
        }
      })()}
    </div>
  )
}

const useMessageTemplateErrorToast = () => {
  const { toast } = useToast()

  return useCallback<ErrorToastHandler>(
    (opts) => {
      toast({
        title: opts.title,
        description: opts.description,
        variant: 'destructive',
      })
    },
    [toast],
  )
}

const MessageTemplateEdit = (props: { messageTemplate: IMessageTemplate }) => {
  const t = useTranslation()
  const statusChecked = useMemo(
    () => messageTemplateSwitchCheckedByStatus[props.messageTemplate.status],
    [props.messageTemplate.status],
  )
  const { toast } = useToast()
  const onErrorToast = useMessageTemplateErrorToast()
  const onUpdateMessageTemplateStatusSuccess = useCallback(() => {
    toast({
      title: t('Estado de la plantilla de mensaje actualizado'),
      description: t(
        'El estado de la plantilla de mensaje ha sido actualizado correctamente',
      ),
      variant: 'success',
    })
  }, [t, toast])
  const messageTemplateEditProps = useMemo(
    () => ({
      messageTemplate: props.messageTemplate,
      onErrorToast,
      onUpdateMessageTemplateStatusSuccess,
    }),
    [onErrorToast, onUpdateMessageTemplateStatusSuccess, props.messageTemplate],
  )
  const {
    debounceSwitch,
    formId,
    isLoading,
    isStatusLoading,
    onUpdateMessageTemplate,
  } = useMessageTemplateEdit(messageTemplateEditProps)

  return (
    <div className='relative'>
      {props.messageTemplate.gatewayStatus && (
        <div>
          <MessageTemplateGatewayStatus
            gatewayStatus={props.messageTemplate.gatewayStatus}
          />
        </div>
      )}

      <div className='top-0 right-0 absolute'>
        <div className='flex items-center'>
          <label className='mb-0 mr-2 text-sm' htmlFor='email-alerts'>
            {t('Activar')}
          </label>
          <Switch
            size='md'
            defaultChecked={statusChecked}
            disabled={isStatusLoading}
            onCheckedChange={debounceSwitch}
          />
        </div>
      </div>

      <div>
        <MessageTemplateEditorV2
          gatewayStatus={props.messageTemplate.gatewayStatus}
          defaultValues={props.messageTemplate.template}
          onSubmit={(data) =>
            onUpdateMessageTemplate(data, () => {
              toast({
                description: t('Plantilla de mensaje actualizada'),
                variant: 'success',
              })
            })
          }
          formId={formId}
          channel={props.messageTemplate.channel}
        >
          <Button
            className='mt-4'
            loading={isLoading}
            disabled={isLoading}
            type='submit'
            form={formId}
            size='sm'
          >
            {t('Guardar')}
          </Button>
        </MessageTemplateEditorV2>
      </div>
    </div>
  )
}

const MultipleMessageTemplatesDetail = (props: {
  messageTemplates: IMessageTemplate[]
  channel: IMessageTemplate['channel']
}) => {
  const t = useTranslation()
  const [currentMessageTemplateId, setCurrentMessageTemplateId] = useState('')

  const currentMessageTemplate = props.messageTemplates.find(
    (messageTemplate) => messageTemplate._id === currentMessageTemplateId,
  )

  return (
    <div>
      <div>
        <div className='flex flex-row items-center'>
          <label>{t('Selecciona una plantilla:')}</label>
          <Select onValueChange={setCurrentMessageTemplateId}>
            <SelectTrigger className='max-w-2xl w-full bg-white'>
              <SelectValue placeholder={t('Ninguna seleccionada')} />
            </SelectTrigger>
            <SelectContent>
              {props.messageTemplates.map((messageTemplate) => {
                return (
                  <SelectItem
                    key={messageTemplate._id}
                    value={messageTemplate._id}
                  >
                    {messageTemplate._id}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
        </div>
        <CreateMessageTemplateV2 channel={props.channel} />
      </div>

      {currentMessageTemplate && (
        <div>
          <Separator className='my-10' />
          <div>
            <p className='text-gray-700 font-bold text-2xl'>{t('EDITOR')}</p>
            <MessageTemplateEdit
              key={currentMessageTemplate._id}
              messageTemplate={currentMessageTemplate}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export const MessageTemplatesV2 = (props: {
  messageTemplates: IMessageTemplate[]
  currentChannel: (typeof deliveryChannels)[number]
}) => {
  const t = useTranslation()
  const onErrorToast = useMessageTemplateErrorToast()
  const {
    currentMessageTemplates,
    onCreateMessageTemplate,
    setTabIndex,
    isLoading,
    tabIndex,
    channels,
    multiple,
    createMessageTemplate,
    setCreateMessageTemplate,
    formId,
  } = useMessageTemplate({
    messageTemplates: props.messageTemplates,
    currentChannel: props.currentChannel,
    onErrorToast,
  })

  const { toast } = useToast()

  return (
    <div>
      <TimelineStyles />
      <div>
        <div className='mt-5 min-w-[20rem] p-6 bg-white rounded'>
          <div className='mt-5' key={props.currentChannel}>
            {!currentMessageTemplates.length ? (
              <div className='flex flex-col w-full items-start justify-center'>
                <MessageTemplateEditorV2
                  onSubmit={(data) =>
                    onCreateMessageTemplate(data, props.currentChannel, () => {
                      toast({
                        description: t('Plantilla de mensaje creada'),
                        variant: 'success',
                      })
                    })
                  }
                  formId={formId}
                  channel={props.currentChannel}
                >
                  <Button
                    type='submit'
                    disabled={isLoading}
                    loading={isLoading}
                    form={formId}
                    variant='primary'
                    className='w-24'
                    size='sm'
                  >
                    {t('Guardar')}
                  </Button>
                </MessageTemplateEditorV2>
              </div>
            ) : multiple ? (
              <MultipleMessageTemplatesDetail
                channel={props.currentChannel}
                messageTemplates={currentMessageTemplates}
              />
            ) : (
              <MessageTemplateEdit
                key={currentMessageTemplates[0]._id}
                messageTemplate={currentMessageTemplates[0]}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
